import { Dictionary, IUserInfo, IWithDuration, MaybeDate, ProgressCalculationType, TaskType } from "../../entities/common";
import { getDurationBetween, addDuration, subtractDuration, max, min, getWorkingHoursForDuration, dayStartMinutesFor, dayEndMinutesFor, getWorkingTimeRanges, minutesOfDay, hasWorkingDayFrom, toOwnDayStart, MAX_DURATION_WALK_DAYS } from '../common/timeline/utils';
import { nameof } from '../../store/services/metadataService';
import { toUniversalDateTime } from "./common";
import { DictatedDates, LoadedTask, TaskScheduleCalculator } from "../task/Fields";
import { createResourceCalendar, Resource } from "../../store/ResourcesListStore";
import { createResourceCapacity, FULL_CAPACITY } from "../../services/TimephasedValueService";
import { IPredecessorInfo, ITask, ITaskAttrs, PredecessorType } from "../../entities/Subentities";
import type { ProjectInfo } from "../../store/ProjectsListStore";
import { CalendarDataSet } from "../../store/calendar/common";

// Tolerance for comparing decimal working-day durations: Duration is rounded to 2 decimals
// (see getDurationBetween), so anything within this bound is treated as an exact match.
export const DURATION_COMPARISON_EPSILON = 0.005;

export function buildPredecessorSnapshot(
    type: PredecessorType,
    attrs: Pick<ITaskAttrs, 'StartDate' | 'DueDate' | 'Duration'>
): Pick<IPredecessorInfo, 'startDate' | 'dueDate' | 'duration'> {
    const isFromStart = type === PredecessorType.StartToStart || type === PredecessorType.StartToFinish;
    return {
        startDate: isFromStart || !attrs.DueDate ? attrs.StartDate : undefined,
        dueDate: !isFromStart || !attrs.StartDate ? attrs.DueDate : undefined,
        duration: type === PredecessorType.FinishToStart ? (attrs.Duration ?? undefined) : undefined,
    };
}

export function applyPredecessors(
    task: ITask,
    predecessors: IPredecessorInfo[] | undefined,
    tasksMap: Dictionary<LoadedTask>,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>, calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true)
    : Partial<IWithDuration> | undefined {
    const data = task.attributes;
    const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, data.AssignedTo, calendar, resources, calendarsById);
    const dictatedDates = TaskScheduleCalculator.GetDictatedDates(
        predecessors,
        tasksMap,
        calendar,
        assigneeCalendars,
        shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay,
        task);

    if (dictatedDates.startDate && dictatedDates.dueDate && data.Duration) {
        const dictatedDueDateByStartDateAndDuration = calculateDueDate(dictatedDates.startDate, data.Duration, calendar, assigneeCalendars);
        if (dictatedDueDateByStartDateAndDuration >= dictatedDates.dueDate) {
            dictatedDates.dueDate = dictatedDueDateByStartDateAndDuration;
        }
        else {
            dictatedDates.startDate = calculateStartDate(dictatedDates.dueDate, data.Duration, calendar, assigneeCalendars);
        }
    }

    if (!!dictatedDates?.startDate || !!dictatedDates?.dueDate) {
        const patch: Dictionary<any> = {};
        if (dictatedDates?.startDate) {
            patch[nameof<ITaskAttrs>("StartDate")] = dictatedDates.startDate.toUniversalIsoDateTimeString();
        }
        if (dictatedDates?.dueDate) {
            patch[nameof<ITaskAttrs>("DueDate")] = dictatedDates.dueDate.toUniversalIsoDateTimeString();
        }
        return { ...patch, ...handleDuration(data, patch, calendar, resources, calendarsById, useResourceCalendar, true) };
    }

    // US 16555: no predecessor dictates the Start anymore (e.g. the FS predecessor whose mid-day finish pushed
    // the Start to noon was removed). A task Start is only ever off the working-day start because a predecessor
    // dictated it, so revert it to the working-day start and recompute the Due from Duration. Without this the
    // successor stays frozen at the old mid-day Start. Mirrors NormalizeUndictatedStartAfterPredecessorChange.
    const startDate = toUniversalDateTime(data.StartDate);
    if (startDate) {
        // Bug 17135: the working-day start comes from the TASK calendar (project + assignees), like the server's
        // ToDayStartFor. On the project calendar alone a disjoint assignee work week (a Sat–Sun resource) has no
        // ranges for the very day the task sits on, so this would drag a legitimate Start off the day start.
        const dayStart = startDate.clone().setTimeOfDay(dayStartMinutesFor(startDate, assigneeCalendars, calendar));
        if (dayStart.getTime() !== startDate.getTime()) {
            const patch = { [nameof<ITaskAttrs>("StartDate")]: dayStart.toUniversalIsoDateTimeString() };
            return { ...patch, ...handleDuration(data, patch, calendar, resources, calendarsById, useResourceCalendar, true) };
        }
    }
}

export function handleDuration(data: ITaskAttrs, update: Partial<ITaskAttrs>, calendar: CalendarDataSet, resources: Dictionary<Resource>, calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean, preserveDuration?: boolean, dictatedDates?: DictatedDates,
    // Whether the task reprojects onto the assignees' own days (reprojectsOntoAssigneeDays) — only the Duration
    // branch below reads it, and only its two UNDICTATED derivations. Defaults to the extending behaviour, which is
    // what every caller that cannot see the task type wants (a date-only patch never reaches that branch anyway).
    reprojects: boolean = true): Partial<IWithDuration> {
    const assignedTo = (nameof<ITaskAttrs>("AssignedTo") in update ? update.AssignedTo : data.AssignedTo) ?? [];
    const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, assignedTo, calendar, resources, calendarsById);

    const measuredDurationDays = useResourceCalendar && data.AssignedTo?.length && data.StartDate && data.DueDate
        // Measured against the task's CURRENT assignees (not the update's), but via getAssigneeCalendars so a
        // not-yet-loaded resource falls back to the global calendar instead of throwing in createResourceCalendar.
        ? calculateDuration(toUniversalDateTime(data.StartDate)!, toUniversalDateTime(data.DueDate)!, data.Duration, calendar, getAssigneeCalendars(useResourceCalendar, data.AssignedTo, calendar, resources, calendarsById))
        : data.Duration;
    // Bug 17148 – a measured 0 over a non-zero stored Duration means those calendars cannot see the span AT ALL
    // (every day of [Start, Due] is a non-working day for them), not that the task is zero-length — a real
    // zero-length task has Duration 0 stored too. It happens whenever the assignees being measured do not share a
    // single working day with the current span, most visibly when the caller has already merged the NEW assignees
    // into `data` (the task panel does) and the incoming resource has a DISJOINT work week: assigning a weekend-only
    // resource to a Mon–Fri task measured 0, and the AssignedTo branch below then derived Due = Start and wrote
    // Duration 0, collapsing the task. Keep the stored Duration, which is what the span is worth.
    const durationDays = measuredDurationDays === 0 && data.Duration ? data.Duration : measuredDurationDays;

    const extra: Partial<IWithDuration> = {};
    if (nameof<IWithDuration>("StartDate") in update) {
        const { startDate, dueDate } = adjustStartDueDatesToAssigneeCalendars(update.StartDate, data.DueDate, useResourceCalendar, calendar, assigneeCalendars, extra, durationDays);
        const newDueDate = toUniversalDateTime(update.DueDate);
        if (!startDate) {
            extra.Duration = null;
        } else if (newDueDate) {
            extra.Duration = getDurationBetween(startDate, newDueDate, assigneeCalendars, calendar);
        } else if (durationDays !== undefined && durationDays !== null && (preserveDuration || !dueDate)) {
            const calculatedDueDate = calculateMovedDueDate(startDate, durationDays, calendar, assigneeCalendars);
            extra.DueDate = calculatedDueDate.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(startDate, calculatedDueDate, durationDays, calendar, assigneeCalendars);
        } else if (dueDate) {
            extra.Duration = calculateDuration(startDate, dueDate, durationDays, calendar, assigneeCalendars);
        }
        return extra;
    }
    if (nameof<IWithDuration>("DueDate") in update) {
        const { startDate, dueDate } = adjustStartDueDatesToAssigneeCalendars(data.StartDate, update.DueDate, useResourceCalendar, calendar, assigneeCalendars, extra, durationDays);
        if (!dueDate) {
            extra.Duration = null;
        } else if (durationDays !== undefined && durationDays !== null && (preserveDuration || !startDate)) {
            const newStartDate = calculateMovedStartDate(dueDate, durationDays, calendar, assigneeCalendars);
            extra.StartDate = newStartDate.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(newStartDate, dueDate, durationDays, calendar, assigneeCalendars);
        } else if (startDate) {
            extra.StartDate = startDate.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(startDate, dueDate, durationDays, calendar, assigneeCalendars);
        }
        return extra;
    }
    if (nameof<ITaskAttrs>("AssignedTo") in update) {
        if(!useResourceCalendar){
            return extra;
        }
        const newAssigneeCalendars = getAssigneeCalendars(useResourceCalendar, update.AssignedTo, calendar, resources, calendarsById);
        // Does the STORED schedule survive the new assignee set, or does it have to be re-derived? Mirrors the
        // server’s IsStoredScheduleRejectedFor, which asks it of the span as STORED — before any re-seating. Asking
        // it of the span that adjustStartDueDatesToAssigneeCalendars has already re-seated answers a different
        // question: shifting a Due off a day the new set does not work lands it on their next working day, which
        // makes an over-long span measure the stored Duration exactly and read as "still fits". Removing the Wed–Fri
        // resource from a Tue→Thu 3d task shared with a Mon–Wed one previewed Tue→Mon / 3d at 100% Units where the
        // engine narrows to the survivor’s own two days and stores Tue→Wed / 2d at 150%.
        const storedStart = toUniversalDateTime(data.StartDate);
        const storedDue = toUniversalDateTime(data.DueDate);
        const storedScheduleFits = !!storedStart && !!storedDue && durationDays !== undefined && durationDays !== null
            && storedStart.isWorkingDay(...newAssigneeCalendars)
            // A zero-day milestone stores Due == Start, a span that measures 0 by construction (see calculateDuration).
            && (durationDays === 0
                ? storedStart.getBeginOfDay().getTime() === storedDue.getBeginOfDay().getTime()
                : Math.abs(getDurationBetween(storedStart, storedDue, newAssigneeCalendars, calendar) - durationDays) <= DURATION_COMPARISON_EPSILON);
        const { startDate, dueDate } = adjustStartDueDatesToAssigneeCalendars(data.StartDate, data.DueDate, useResourceCalendar, calendar, newAssigneeCalendars, extra, durationDays);
        if (storedScheduleFits && startDate && dueDate && durationDays !== undefined && durationDays !== null) {
            extra.StartDate = startDate.toUniversalIsoDateTimeString();
            extra.DueDate = dueDate.toUniversalIsoDateTimeString();
            extra.Duration = durationDays;
        } else if (startDate && durationDays !== undefined && durationDays !== null) {
            // The stored span no longer measures the Duration on the new assignee calendars, so re-seat it the way
            // the server does (AdjustScheduleDataUpdates, then RepairScheduleForAssigneeChange) — in that order:
            //  1. NARROW the Duration to the span’s REAL duration on the NEW calendars — the per-assignee MIN, capped
            //     at the stored value so it only ever shrinks. Keeping the stored count LENGTHENS the task by the days
            //     only a departing resource worked.
            //  2. Derive the Due with the COMBINED walk — the exact inverse of the span check, so the triple is
            //     consistent by construction — and NOT with the per-assignee MAX. That MAX is the span EXTENSION,
            //     which belongs to the effort reprojection running AFTER this and off the Duration settled here;
            //     doing it here as well extends TWICE (Bug 17140).
            //
            // Three payloads do NOT narrow:
            //  • Task 17151 – nobody left assigned. There is no per-assignee minimum to take, so the stored Duration
            //    stands, the same fallback the server’s GetRealDuration makes on an empty calendar collection.
            //    getAssigneeCalendars substitutes the GLOBAL calendar for an empty set, which would narrow the count
            //    to the project’s own working days inside a span whose days were the DEPARTED resource’s. Asked of
            //    the assignee LIST rather than the calendars, because a resource whose calendar merely failed to LOAD
            //    gets that same stand-in and there it IS the calendar to measure on.
            //  • Bug 17153 – a FIRST assignment, for the mirror-image reason: the stored span's days belong to no
            //    departed resource, so its Duration is already the real one and the incoming resource simply needs
            //    that many of their own days. Mirrors the server’s hadPriorAssignments gate.
            //  • Bug 17156 – the MIN is taken over the span's OWNERS (getSpanOwners): the new set AND whoever this
            //    edit removes. The real duration lives in the calendar of the assignee that dictated the Due, so when
            //    THAT one leaves the survivors' counts are all larger and the narrowing becomes a lengthening. Only
            //    the day COUNT comes from the wider set — the Due below still walks the NEW calendars.
            const spanDuration = storedStart && storedDue && !!update.AssignedTo?.length && !!data.AssignedTo?.length
                ? getRealDuration(storedStart, storedDue, durationDays,
                    getAssigneeCalendars(useResourceCalendar, getSpanOwners(data.AssignedTo, update.AssignedTo), calendar, resources, calendarsById), calendar)
                : durationDays;
            const newDueDate = calculateMovedDueDate(startDate, spanDuration, calendar, newAssigneeCalendars);
            extra.StartDate = startDate.toUniversalIsoDateTimeString();
            extra.DueDate = newDueDate.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(startDate, newDueDate, spanDuration, calendar, newAssigneeCalendars);
        } else if (dueDate && durationDays !== undefined && durationDays !== null) {
            const newStartDate = calculateStartDate(dueDate, durationDays, calendar, newAssigneeCalendars);
            extra.StartDate = newStartDate.toUniversalIsoDateTimeString();
            extra.DueDate = dueDate.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(newStartDate, dueDate, durationDays, calendar, newAssigneeCalendars);
        } else if (startDate && dueDate) {
            extra.StartDate = startDate.toUniversalIsoDateTimeString();
            extra.DueDate = dueDate.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(startDate, dueDate, durationDays, calendar, newAssigneeCalendars)
        }
        return extra;
    }
    if (nameof<IWithDuration>("Duration") in update) {
        const startDate = toUniversalDateTime(data.StartDate);
        const dueDate = toUniversalDateTime(data.DueDate);
        const newDurationDays = update.Duration;
        if (newDurationDays === undefined || newDurationDays === null) {
            if (startDate && dictatedDates?.dueDate && !dictatedDates?.startDate) {
                extra.StartDate = null;
            } else if (startDate) {
                extra.DueDate = null;
            }
        } else if (!startDate && !dueDate) {
            return extra;
        } else if (startDate && !dictatedDates?.dueDate) {
            const dictatedStart = dictatedDates?.startDate;
            const followDictated = !!dictatedStart && (dictatedStart > startDate || (newDurationDays === 0 && dictatedStart < startDate));
            const anchorStart = followDictated ? dictatedStart! : startDate;
            if (newDurationDays === 0) {
                extra.StartDate = anchorStart.toUniversalIsoDateTimeString();
                extra.DueDate = anchorStart.toUniversalIsoDateTimeString();
                extra.Duration = 0;
            } else {
                // EXTEND only when the engine will reproject the rows onto each assignee's own days — calculateDueDate's
                // per-assignee MAX walk IS that extension, folded into the derivation (the server derives on the
                // combined calendar and lets SyncTotalsAndExtendSpanForReprojection push the Due out afterwards, which
                // lands on the same date). With no reprojection there is nothing to push it out, so the count must be
                // walked on the assignee UNION: Duration 3 from Thu 08-20 for a Mon–Tue–Wed + Wed–Thu–Fri pair with no
                // Effort previewed Due 08-26 / 5d (each resource's own third day) where the engine stores 08-24 / 3d.
                const newDueDate = reprojects
                    ? calculateDueDate(anchorStart, newDurationDays, calendar, assigneeCalendars)
                    : calculateMovedDueDate(anchorStart, newDurationDays, calendar, assigneeCalendars);
                extra.StartDate = anchorStart.toUniversalIsoDateTimeString();
                extra.DueDate = newDueDate.toUniversalIsoDateTimeString();
                extra.Duration = calculateDuration(anchorStart, newDueDate, newDurationDays, calendar, assigneeCalendars);
            }
        } else if (dueDate && !dictatedDates?.startDate) {
            // Mirror image of the Due derivation above: the span hangs from the FINISH and grows backwards.
            //
            // Bug 17223 – anchored on the DICTATED finish when a to-Finish predecessor names one, not on the Due the
            // task happens to carry. The two coincide on a task the engine last saved, and diverge the moment they
            // do not: an SF predecessor dictates the successor's finish at the predecessor's START — a day-START
            // instant, which is an EXCLUSIVE finish — where the stored Due still sat at the end of that day, so the
            // preview derived a Start a day late and a Duration its own dates did not measure. With no dictated Due
            // at all this is the "task has a Due but no Start" case, where the stored Due IS the anchor.
            //
            // A dictated anchor is walked with the COMBINED calendar, mirroring the engine's CalculateSpanStartDate:
            // that walk is the exact inverse of the span check, so the triple it produces passes by construction.
            // Without one, the per-assignee MIN still applies whenever the rows will be reprojected.
            const dictatedDue = dictatedDates?.dueDate;
            const anchorDue = dictatedDue ?? dueDate;
            const newStartDate = dictatedDue || !reprojects
                ? calculateMovedStartDate(anchorDue, newDurationDays, calendar, assigneeCalendars)
                : calculateStartDate(anchorDue, newDurationDays, calendar, assigneeCalendars);
            extra.StartDate = newStartDate.toUniversalIsoDateTimeString();
            extra.DueDate = anchorDue.toUniversalIsoDateTimeString();
            extra.Duration = calculateDuration(newStartDate, anchorDue, newDurationDays, calendar, assigneeCalendars);
        } else if (startDate && dueDate && dictatedDates?.startDate && dictatedDates?.dueDate) {
            const oldDuration = durationDays ?? getDurationBetween(startDate, dueDate, assigneeCalendars, calendar);
            if (newDurationDays > oldDuration) {
                shiftStartDate(dueDate, newDurationDays, dictatedDates.startDate, calendar, assigneeCalendars, extra);
            } else {
                shiftDueDate(startDate, newDurationDays, dictatedDates.dueDate, calendar, assigneeCalendars, extra);
            }
        } else if (dueDate && dictatedDates?.startDate) {
            shiftStartDate(dueDate, newDurationDays, dictatedDates.startDate, calendar, assigneeCalendars, extra);
        } else if (startDate && dictatedDates?.dueDate) {
            shiftDueDate(startDate, newDurationDays, dictatedDates.dueDate, calendar, assigneeCalendars, extra);
        }
    }
    return extra;
}

// Bug 17135: `addWorkingDays` keeps the TIME-OF-DAY of the day it came from, but the day it lands on may be measured
// by a different calendar entirely — the 10:00–18:00 weekend a Sat–Sun resource works and the project calendar knows
// nothing about, or the project's own 08:00–17:00 weekday once that resource is removed. So re-stamp the moved
// endpoint from the calendars that now govern the task (dayStartMinutesFor/dayEndMinutesFor, the same union the
// duration walks measure a day with, and what the server's ToDayStartFor does). Only ON A DAY SHIFT: an endpoint
// already sitting on a workable day may legitimately be mid-day because a predecessor dictated it.
function adjustStartDueDatesToAssigneeCalendars(startDateObj: MaybeDate, dueDateObj: MaybeDate, useResourceCalendar: boolean, globalCalendar: CalendarDataSet, assigneeCalendars: CalendarDataSet[], extra: Partial<IWithDuration>, durationDays?: number | null) {
    let startDate = toUniversalDateTime(startDateObj);
    if (startDate && useResourceCalendar && !startDate.isWorkingDay(...assigneeCalendars)) {
        const shifted = startDate.addWorkingDays(1, ...assigneeCalendars);
        startDate = shifted.setTimeOfDay(dayStartMinutesFor(shifted, assigneeCalendars, globalCalendar));
        extra.StartDate = startDate.toUniversalIsoDateTimeString();
    }

    let dueDate = toUniversalDateTime(dueDateObj);
    if (dueDate && useResourceCalendar && !dueDate.isWorkingDay(...assigneeCalendars)) {
        const shifted = dueDate.addWorkingDays(1, ...assigneeCalendars);
        // A zero-duration milestone keeps Due == Start (the day start); anything else takes the new day's end.
        dueDate = shifted.setTimeOfDay(durationDays === 0
            ? dayStartMinutesFor(shifted, assigneeCalendars, globalCalendar)
            : dayEndMinutesFor(shifted, assigneeCalendars, globalCalendar));
        extra.DueDate = dueDate.toUniversalIsoDateTimeString();
    }
    return { startDate, dueDate };
}

// Bug 17141 – does the engine REPROJECT this task, i.e. hand every assignee `Duration` days of THEIR OWN calendar and
// let the span follow those rows? Only then does a Duration ↔ date derivation EXTEND (the per-assignee MAX walk);
// otherwise it is a plain combined-calendar walk. RecalculateEffort's UC2 guard skips the whole reprojection when
// there is no Effort to distribute AND nothing derives one — which is every task type except an AUTO Fixed Units one,
// where Effort IS derived from the span (recomputeEffort). An Effort of 0 is a VALUE and gets distributed, so this
// tests for null, not for a positive number.
export function reprojectsOntoAssigneeDays(task: ITask): boolean {
    return task.attributes.Effort != null || (task.taskType === TaskType.FixedUnits && !!task.isAutoMode);
}

export function getAssigneeCalendars(useResourceCalendar: boolean, assignedTo: IUserInfo[] | undefined, calendar: CalendarDataSet, resources: Dictionary<Resource>, calendarsById: Dictionary<CalendarDataSet>) {
    if (!assignedTo || assignedTo.length === 0) {
        return [calendar];
    }
    // US 15956 (ID 11): capacity is per assignee and reaches the DATE walks (a day with none is non-working),
    // which the single shared global calendar cannot express. Take the per-assignee route only when someone
    // actually has a capacity, so the common path keeps its exact previous shape. Mirrors server
    // GetAssigneeCalendars.
    if (!useResourceCalendar && assignedTo.every(_ => isFullCapacity(resources[_.id]))) {
        return [calendar];
    }
    return assignedTo.map(_ => schedulingCalendar(resources[_.id], calendarsById, calendar, useResourceCalendar));
}

// The calendar an assignee's schedule is measured on: their work week (or the global one) carrying their capacity,
// so every walk that reads it - hours AND dates - sees the same working days. Mirrors server GetResourceCalendar.
function schedulingCalendar(resource: Resource | undefined, calendarsById: Dictionary<CalendarDataSet>, calendar: CalendarDataSet, useResourceCalendar: boolean): CalendarDataSet {
    // Fall back to the global calendar for a not-yet-loaded resource (createResourceCalendar throws on undefined).
    const base = useResourceCalendar && resource 
        ? createResourceCalendar(resource, calendarsById[resource.calendar.id]) 
        : calendar;
    const capacity = createResourceCapacity(resource);
    // A copy whenever a capacity is attached: the global calendar is shared and must not acquire one.
    return capacity === FULL_CAPACITY ? base : { ...base, capacityFactor: capacity.factorOn, capacityKey: capacity.key };
}

function isFullCapacity(resource: Resource | undefined): boolean {
    return createResourceCapacity(resource) === FULL_CAPACITY;
}

// Truncate toward zero at 2 decimal places, mirroring server decimal.Round(_, 2, MidpointRounding.ToZero).
function truncate2(value: number): number {
    return Math.trunc(value * 100) / 100;
}

// US 15956: an assignee's per-day capacity contribution — their calendar paired with their Units factor
// (Units% / 100). Mirrors server AssigneeCapacity.
interface AssigneeCapacity {
    calendar: CalendarDataSet;
    unitsFactor: number;
}

// US 15956 (ID 11): the calendar already reports CAPACITY hours — its capacityFactor carries Max Units /
// Non-project Work — so these only have to add the Units on top. capacityHoursOver leaves them off: that is the
// capacity Units are measured against. Mirrors server AssigneeCapacity.
function hoursOn(c: AssigneeCapacity, day: Date): number {
    return day.getWorkingHours(c.calendar) * c.unitsFactor;
}

function hoursOver(c: AssigneeCapacity, startDate: Date, duration: number, globalCalendar: CalendarDataSet): number {
    return capacityHoursOver(c, startDate, duration, globalCalendar) * c.unitsFactor;
}

// Task 17266 – anchored in the assignee's OWN day (toOwnDayStart), as the server's AssigneeCapacity.HoursOver is: a
// task that occupies its first day whole is worth that assignee's whole day, however their shift is offset against
// the project's.
function capacityHoursOver(c: AssigneeCapacity, startDate: Date, duration: number, globalCalendar: CalendarDataSet): number {
    return getWorkingHoursForDuration(toOwnDayStart(startDate, c.calendar, globalCalendar), duration, c.calendar);
}

// US 15956 / Bug 17193 / Bug 17202 – the assignees the engine will actually give effort ROWS to, and therefore the
// only ones that may take a SHARE of the Effort. Mirrors the server's AssigneeCapacity.CarriesEffortFrom: Units above
// 0 (a 0%-Units assignee keeps its place in the list but gets no work — UC15/UC16) AND a calendar day to put work on
// at or after the Start, which is what its DistributionStartFor walk answers. A resource with no capacity at all (Max
// Units 0%, 100% Non-project Work, a 0-hour work week) is handed no rows, so counting it as a participant does not
// merely MISPLACE its share — it LOSES it, and everything derived from the split (the per-assignee Units, the span)
// then disagrees with what the server stores. Asked of both task types: RecalculateEffort's equal split excludes
// these assignees whatever the task type is, and Flexible Units are derived from the rows it leaves behind.
function carriesEffortFrom(startDate: Date, capacity: AssigneeCapacity): boolean {
    return capacity.unitsFactor > 0 && hasWorkingDayFrom(startDate, [capacity.calendar]);
}

// US 15956: an assignment's Units (%) as a capacity multiplier; null/undefined ⇒ the 100% default.
function getUnitsFactor(assignee: IUserInfo): number {
    return (assignee.units ?? 100) / 100;
}

// One capacity entry per assignee — the resource calendar when enabled, else the global calendar for each —
// weighted by the assignee's Units, so multi-assignee sums scale correctly (getAssigneeCalendars collapses to a
// single global entry). Matches the server assigneeCalendars map + per-assignment Units.
function getPerAssigneeCapacities(assignees: IUserInfo[], calendar: CalendarDataSet, resources: Dictionary<Resource>, calendarsById: Dictionary<CalendarDataSet>, useResourceCalendar: boolean): AssigneeCapacity[] {
    return assignees.map(a => ({
        // Fall back to the global calendar when the resource isn't loaded yet (e.g. just picked) — createResourceCalendar
        // throws on an undefined resource. The server recomputes with the real calendar on save.
        // An unloaded resource is at FULL capacity, matching the server's missing-resource fallback (ID 11).
        calendar: schedulingCalendar(resources[a.id], calendarsById, calendar, useResourceCalendar),
        unitsFactor: getUnitsFactor(a)
    }));
}

// Working days from the start whose combined assignee capacity yields the given Effort. Walks day by day
// consuming each day's combined expected hours × Units (exception-aware) until Effort is met; the final partial
// day contributes a proportional fraction. Mirrors server CalculateDurationFromEffort.
function getFixedUnitsDuration(startDate: Date, effort: number, capacities: AssigneeCapacity[]): number {
    let remaining = effort;
    let duration = 0;
    const cursor = startDate.clone();
    for (let guard = 0; guard < MAX_DURATION_WALK_DAYS && remaining > 0; guard++) {
        const dayCapacity = capacities.reduce((sum, c) => sum + hoursOn(c, cursor), 0);
        if (dayCapacity > 0) {
            if (remaining <= dayCapacity) {
                duration += remaining / dayCapacity;
                break;
            }
            remaining -= dayCapacity;
            duration += 1;
        }
        cursor.addDays(1);
    }
    return truncate2(duration);
}

// Bug 17132 / Bug 17139 – lays a `getFixedUnitsDuration` span on the assignee's OWN calendar and returns the instant
// they finish — their share's end, of which the task Due is the latest across assignees. Mirrors server
// GetAssigneeSpanEnd.
//
// That span counts the assignee's OWN working days, so its final FRACTION has to be converted back to minutes with
// THEIR day as the unit. calculateDueDate cannot do it: addDuration measures a day with the GLOBAL calendar's working
// times (the convention Duration itself follows), which stretches a short-work-week assignee's fraction over the
// project's longer day and ends the task after that resource stops working.
//
// A WHOLE own-day keeps the project's day-end convention instead (dayEndMinutesFor, what the server's reprojection
// extends a span to): a day the assignee works to the end of is a day the task OCCUPIES, so it counts as a full day
// of Duration. Only the final partial day differs, and nothing changes for an assignee whose hours match the
// project's.
function getAssigneeSpanEnd(startDate: Date, ownDaySpan: number, assigneeCalendar: CalendarDataSet, calendar: CalendarDataSet): Date {
    if (ownDaySpan <= 0) {
        return startDate;
    }
    // Task 17266 – the span counts the assignee's OWN days, so it is walked from the instant THEIR day opens at, or
    // a shift starting before the project's spills the last fraction of a whole-day span onto an extra day.
    const end = addDuration(toOwnDayStart(startDate, assigneeCalendar, calendar), ownDaySpan, [assigneeCalendar], assigneeCalendar);
    const ownRanges = getWorkingTimeRanges(end, assigneeCalendar);
    const landedOnTheirDayEnd = ownRanges.length > 0 && minutesOfDay(end) === ownRanges[ownRanges.length - 1].to;
    return landedOnTheirDayEnd
        ? end.setTimeOfDay(dayEndMinutesFor(end, [assigneeCalendar], calendar))
        : end;
}

type RecalcOptions = {
    unitsEdit?: boolean, spanEdited?: boolean, mayContract?: boolean, effortStated?: boolean, firstAssignment?: boolean,
    /**
     * Bug 17384 – where this step reports the per-assignee shares it settled, for the NEXT step of the replay: a share
     * an earlier step derived (an add gives the newcomer its own) exists in no stored row for it to read.
     */
    sharesUsed?: { value?: Dictionary<number> },
};

/** Everything the Fixed Units / Flexible recalc steps read about the task being previewed. */
type RecalcInputs = {
    /** Bug 17384 – per-assignee shares the caller settled, used instead of inferring them. */
    sharesOverride?: Dictionary<number>,
    task: ITask,
    update: Partial<ITaskAttrs>,
    primary: Partial<ITaskAttrs>,
    assignees: IUserInfo[],
    startDate: Date,
    capacities: AssigneeCapacity[],
    assigneeCalendars: CalendarDataSet[],
    isFixedUnits: boolean,
    scheduling: SchedulingInputs,
    options: RecalcOptions | undefined,
};

/** Which edit is being previewed — read off `primary`, the user's own change. */
type RecalcTriggers = {
    assigneeChanged: boolean,
    membershipChanged: boolean,
    unitsOnlyChange: boolean,
    spanEdited: boolean,
    survivorsKeepTheirEffort: boolean,
};

/** Everything the schedule math reads about the calendars in play. */
export type SchedulingInputs = {
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    /**
     * Bug 17380 – what each assignee ACTUALLY holds of the task Effort, read off its persisted assignment rows, by
     * assignee id. Optional: where they are not loaded the shares are INFERRED instead (see storedSharesById).
     */
    storedShares?: Dictionary<number>,
};

type FirstAssignmentInputs = {
    assignees: IUserInfo[],
    capacities: AssigneeCapacity[],
    assigneeCalendars: CalendarDataSet[],
    calendar: CalendarDataSet,
    startDate: Date,
    dueDate: Date | undefined,
    duration: number,
    effort: number,
};

/**
 * US 15956 UC3 / UC4 – derives each assignee's Units from their equal share of the Effort the task already carries,
 * keeping that total (mirrors server ApplyFixedUnitsRules / DeriveUnitsForFirstAssignment). Units is capped at 100%:
 * an over-100% share keeps 100% and EXTENDS the Duration (UC3-2: 10h/8h -> 1.25d); a fitting share derives sub-100%
 * Units and keeps the Duration (UC3-1: 20h/40h -> 50%; UC4 CSV/API: 2x20h -> 50%).
 */
function deriveUnitsForFirstAssignment(input: FirstAssignmentInputs): Partial<ITaskAttrs> {
    const { assignees, capacities, assigneeCalendars, calendar, startDate, dueDate, duration, effort } = input;

    // Over the assignees that CAN work it, as the server does: a 0%-Units one takes no share and keeps the
    // explicit 0 the user typed. Bug 17202 – "can work it" is the server's CarriesEffortFrom, not the Units test
    // alone, because a resource with no CALENDAR capacity is given no rows either and its share would be LOST.
    // Neither has its Units derived — that divides by a capacity of 0.
    const carriesEffort = capacities.map(c => carriesEffortFrom(startDate, c));
    const contributorCount = carriesEffort.filter(Boolean).length;
    const perAssignee = contributorCount > 0 ? effort / contributorCount : 0;
    // Capacity is measured over the REAL duration — the working days each assignee actually has INSIDE the span
    // — never by walking `Duration` days on their own calendar, which credits days past the Due (getRealDuration).
    // It must also be the basis the SERVER measures with: a create payload carries a Duration, so the server takes
    // its recompute path, and only Units derived against that same MIN span reproduce the entered Effort.
    const basis = getRealDuration(startDate, dueDate, duration, assigneeCalendars, calendar);
    // Bug 17139 – the span extension is tracked as the latest END INSTANT any assignee needs, never as a day
    // COUNT compared against Duration: getFixedUnitsDuration counts the assignee's OWN days while Duration counts
    // the project's (see getAssigneeSpanEnd), so a count cannot answer "does this fit?" for a work week whose
    // daily hours differ from the project's. Mirrors server ApplyFixedUnitsRules.
    const currentDue = dueDate ?? calculateDueDate(startDate, duration, calendar, assigneeCalendars);
    let maxEnd = currentDue;
    const derivedAssignees = assignees.map((a, i) => {
        if (!carriesEffort[i]) {
            return a; // contributes nothing: no share, and its Units are not re-derived
        }
        const capacity = capacityHoursOver(capacities[i], startDate, basis, calendar);
        const rawUnits = capacity > 0 ? perAssignee / capacity : 1;
        if (rawUnits > 1) {
            const span = getFixedUnitsDuration(startDate, perAssignee, [{ calendar: capacities[i].calendar, unitsFactor: 1 }]);
            const end = getAssigneeSpanEnd(startDate, span, capacities[i].calendar, calendar);
            if (end > maxEnd) {
                maxEnd = end;
            }
            return { ...a, units: undefined };
        }
        return { ...a, units: truncate2(rawUnits * 100) };
    });

    const result: Partial<ITaskAttrs> = { AssignedTo: derivedAssignees };
    // EXTEND only — a share that fits keeps the Duration the task came with (UC3-1).
    if (maxEnd > currentDue) {
        result.Duration = getDurationBetween(startDate, maxEnd, assigneeCalendars, calendar);
        result.DueDate = maxEnd.toUniversalIsoDateTimeString();
    }
    return result;
}

// Bug 17373 – what each assignee holds of the task's stored Effort. The engine reads their rows; without them the
// panel reads which SHAPE those rows are in and splits the total accordingly. The two rules that lay them split
// differently: a span-driven recompute gives each assignee Units × capacity over the Duration, an Effort-preserving
// derivation splits EQUALLY and lets each own span fall where it may. Which one a task is in is legible from its
// totals alone — if the per-assignee capacities over the stored span sum to the stored Effort, the rows came from the
// span and the shares are PROPORTIONAL to them (100%+50% over 5d is 40h+20h, never 30h+30h); if they do not, the span
// was stretched past somebody's work and the equal split is the one that reproduces it (the 10d/80h pair of Bug 17370
// is 40h+40h, not 80h+40h).
const storedSharesById = (
    task: ITask,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    storedShares?: Dictionary<number>): Dictionary<number> | undefined => {
    const prior = task.attributes.AssignedTo ?? [];
    const effort = task.attributes.Effort ?? 0;
    // Bug 17380 – the REAL rows when the caller has them. They answer states the inference below cannot: a span
    // stretched by a Units edit leaves each assignee holding what it held, and nothing in the task attributes says
    // what that was (80/40/20 and 100/40/0 both make 140h over the same 10-day span). Same guard the engine puts on
    // its own rows — shares that do not ACCOUNT for the Effort describe some older total, so they are not kept.
    // Bug 17381 – and only while they DESCRIBE this state: the rows belong to the task as STORED, while the replay
    // measures intermediate ones (an add-then-Units session asks about a set that already holds the newcomer). Both
    // tests matter — every assignee covered, and the total accounting for the Effort — and failing either falls
    // through to the inference below rather than to an even split, which is the worse answer of the two.
    const describesThisState = !!storedShares
        && prior.every(a => storedShares[a.id] != null)
        && Math.abs(prior.reduce((sum, a) => sum + storedShares[a.id], 0) - effort) <= DURATION_COMPARISON_EPSILON;
    if (describesThisState) {
        return Object.fromEntries(prior.map(a => [a.id, storedShares![a.id]]));
    }
    const storedStart = toUniversalDateTime(task.attributes.StartDate);
    const storedDuration = task.attributes.Duration;
    if (prior.length === 0 || effort <= 0 || !storedStart || storedDuration == null || storedDuration <= 0) {
        return undefined;
    }
    // Measured over the span's REAL duration — the per-assignee MIN of own working days inside it — because that is
    // the basis the engine derived the shares on (AdjustScheduleDataUpdates' GetRealDuration, mirrored by
    // previewFixedUnitsRecompute's durationForEffort). Bug 17376 – the raw Duration is a UNION count, so a part-week
    // assignee inflates it and the sum test below then rejects rows that are in fact right. The two are the same
    // number whenever every assignee works the whole span, which is why a uniform work week never showed it.
    const capacities = getPerAssigneeCapacities(prior, calendar, resources, calendarsById, useResourceCalendar);
    const storedDue = toUniversalDateTime(task.attributes.DueDate);
    const basis = storedDue && storedDue.getTime() > storedStart.getTime()
        ? getRealDuration(storedStart, storedDue, storedDuration, capacities.map(c => c.calendar), calendar)
        : storedDuration;
    const shares = capacities.map(c => hoursOver(c, storedStart, basis, calendar));
    if (Math.abs(shares.reduce((sum, hours) => sum + hours, 0) - effort) > DURATION_COMPARISON_EPSILON) {
        return undefined; // the span was stretched past somebody's work, so the rows were NOT laid from it
    }
    const byId: Dictionary<number> = {};
    prior.forEach((a, i) => { byId[a.id] = shares[i]; });
    return byId;
};

// What the assignees named in `keepIds` hold of the stored Effort, falling back to an equal split of it.
const storedShareOf = (
    keepIds: string[],
    task: ITask,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    storedShares?: Dictionary<number>): number => {
    const shares = storedSharesById(task, calendar, resources, calendarsById, useResourceCalendar, storedShares);
    const prior = task.attributes.AssignedTo ?? [];
    return shares
        ? truncate2(keepIds.reduce((sum, id) => sum + (shares[id] ?? 0), 0))
        : (task.attributes.Effort ?? 0) * keepIds.length / (prior.length || 1);
};

// US 15958 / US 15956 – client-side mirror of TaskWorkloadHandler.ApplyFixedUnitsRules so the task edit panel
// and the grid inline editor preview the linked Effort/Duration values live before the change is persisted. For
// auto Fixed Units tasks (in an Effort project): Effort = Σ assignees (Units × working hours over the Duration),
// taken from the project/resource calendars so the work-week, exceptions, and per-assignment Units are respected.
//   • A Duration or Due-date change recalculates Effort, keeping the span (ID 8, fractional-day aware).
//   • A resource ADD/REMOVE recalculates Effort, keeping Duration (ID 8/22) — unless Effort Driven is on, which
//     keeps Effort and re-derives Duration instead (ID 21).
//   • A Start move, a predecessor change, a per-assignment Units edit, or an Effort-Driven resource change
//     PRESERVES Effort and re-derives Duration + Due by walking each assignee's calendar capacity.
//   • An Effort edit is deliberately NOT previewed here (returns undefined): the client sends only Effort and
//     the server derives Duration/Due. Effort→Duration (per-assignee-MAX) and Duration→Effort (every assignee
//     works the full Duration) are NOT inverses under per-assignment Units / calendar exceptions, so emitting a
//     client-derived Duration would flip the server into recompute-Effort and inflate it (e.g. 32 → 48).
// The server remains authoritative on save (and resolves sub-day precision on fractional durations); this only
// keeps the panel in sync.
// `primary` is the field the user actually edited; `derived` is any handleDuration/RecalculateByEffort output.
// The RECALC TRIGGER is detected from `primary` only — handleDuration derives Start/Due/Duration for an
// assignee change (under resource calendars), and those must not be mistaken for a user Duration/date edit.
// `options.unitsEdit` says the assignee change is an in-place per-assignment UNITS edit (the caller knows the assignee
// set the user edited FROM; see the membership check below).
// `options.spanEdited` says the span in `derived` was already reconciled against an explicit Duration/Due VALUE, even
// though that is not the edit being applied now. It only differs for a caller whose `derived` covers more edits than
// `primary` (the panel, where `changes` accumulates and `primary` is just the latest of them) and it is what keeps the
// per-assignee span EXTENSION from running twice: handleDuration derives the Due of a Duration edit with
// calculateDueDate, whose per-assignee MAX walk already performs that extension.
// `options.mayContract` is the engine's `contractSpanToEffort`: may a span re-derived from the effort rows end EARLIER
// than the one reconciliation left behind? It is the caller's answer because it turns on a predecessor-dictated Due,
// which only the panel can see. Defaults to true, which is what a bare move gets.
export function applyFixedUnitsRecalc(
    project: ProjectInfo,
    task: ITask,
    primary: Partial<ITaskAttrs>,
    derived: Partial<ITaskAttrs> | undefined,
    scheduling: SchedulingInputs,
    options?: RecalcOptions): Partial<ITaskAttrs> | undefined {

    const { calendar, resources, calendarsById, useResourceCalendar, storedShares } = scheduling;
    const unitsEdit = options?.unitsEdit;

    // Runs for both task types: Fixed Units uses the full Effort↔Duration recalc; Flexible only previews the
    // Units-edit → Duration case (its other triggers derive Units server-side). Auto + Effort-project + leaf only.
    if (!task.isAutoMode
        || task.hierarchy.isParent
        || project.settings.progressCalculationType !== ProgressCalculationType.Effort) {
        return undefined;
    }
    const isFixedUnits = task.taskType === TaskType.FixedUnits;

    // Merged view for reading the resulting values (dates/duration handleDuration produced).
    const update = { ...primary, ...derived };

    const assignees = (nameof<ITaskAttrs>("AssignedTo") in update ? update.AssignedTo : task.attributes.AssignedTo) ?? [];
    const startDate = toUniversalDateTime(nameof<ITaskAttrs>("StartDate") in update ? update.StartDate : task.attributes.StartDate);
    if (assignees.length === 0 || !startDate) {
        return undefined;
    }

    const capacities = getPerAssigneeCapacities(assignees, calendar, resources, calendarsById, useResourceCalendar);
    const assigneeCalendars = capacities.map(c => c.calendar); // for date math (units-independent)

    // ID 21: with Effort Driven ON, a pure resource change keeps Effort constant and adjusts Duration —
    // but only after the initial assignment (ID 21.1), approximated by the task already having assignees
    // and a computed Effort (the server is authoritative via the assignment rows).
    const assigneeChanged = nameof<ITaskAttrs>("AssignedTo") in primary;
    const assigneeOnlyChange = assigneeChanged
        && !(nameof<ITaskAttrs>("Duration") in primary)
        && !(nameof<ITaskAttrs>("StartDate") in primary)
        && !(nameof<ITaskAttrs>("DueDate") in primary);
    const hadPriorAssignees = (task.attributes.AssignedTo?.length ?? 0) > 0;
    const effortDrivenResourceChange = task.effortDriven && assigneeOnlyChange && hadPriorAssignees && (task.attributes.Effort ?? 0) > 0;

    // US 15956: tell an assignee ADD/REMOVE apart from an in-place Units edit on the same assignees (mirrors the
    // server membership check against the persisted assignees). A Units-only edit PRESERVES Effort and re-derives
    // Duration (ID 3.3 / ID 6); an add/remove recomputes Effort (unless Effort Driven).
    // `unitsEdit` is the caller's answer, since it knows the assignee set the edit started FROM (the panel's previous
    // state), and the id comparison alone misreads two cases: on the CREATE panel the "persisted" task has NO
    // assignees, so every Units edit looks like a first assignment; and after an add-then-REMOVE round trip the ids
    // match again while the payload carries the DISPLAYED (derived) Units, which looked like an in-place Units edit.
    const oldIds = new Set((task.attributes.AssignedTo ?? []).map(a => a.id));
    const newIds = assignees.map(a => a.id);
    // Only an unsaved task needs the caller to override the comparison; on a stored one a real membership change wins
    // (the server re-derives Units across one, so honouring sent Units would disagree with what it will store).
    const membershipChanged = unitsEdit && !task.id
        ? false
        : newIds.length !== oldIds.size || newIds.some(id => !oldIds.has(id));
    const unitsOnlyChange = assigneeChanged && !membershipChanged && (unitsEdit ?? hadPriorAssignees);

    // US 15956 UC3 / UC4: the FIRST assignment to a Fixed Units task that already has Duration + Effort derives each
    // assignee's Units from their equal Effort share, keeping total Effort (mirrors server ApplyFixedUnitsRules).
    // Units is capped at 100%: an over-100% share keeps 100% and extends the Duration (UC3-2: 10h/8h → 1.25d); a
    // fitting share derives sub-100% Units and keeps the Duration (UC3-1: 20h/40h → 50%; UC4 CSV/API: 2×20h → 50%).
    // Adds to an already-assigned task recompute (UC5).
    // "Already has Duration + Effort" is read from the MERGED state (`update` first, task only as the fallback), the
    // way the server's counterpart reads the entity AFTER the update was applied. On the CREATE panel the stored task
    // is a set of defaults, so the Effort/Duration this assignment must respect are the ones the user just typed.
    const uc3Effort = (nameof<ITaskAttrs>("Effort") in update ? update.Effort : task.attributes.Effort) ?? 0;
    const uc3Duration = nameof<ITaskAttrs>("Duration") in update ? update.Duration : task.attributes.Duration;
    // Bug 17180 – a first assignment that includes a 0%-Units assignee lands on these rules too, because the
    // server's 0%-Units redistribution has no prior shares to drop. The panel replays the add and the Units edit as
    // two steps, so by the second one `hadPriorAssignees` is true against its own step-1 state — hence
    // `firstAssignment`, answered from the STORED task. Only with an active assignee left: an all-zeroed one is
    // UC15's collapse, which the 0%-Units rules keep.
    const firstAssignmentWithZeroUnits = isFixedUnits && (options?.firstAssignment ?? false)
        && assignees.some(a => getUnitsFactor(a) <= 0)
        && assignees.some(a => getUnitsFactor(a) > 0);
    if (isFixedUnits && assigneeChanged && ((membershipChanged && !hadPriorAssignees) || firstAssignmentWithZeroUnits)
        && uc3Effort > 0 && uc3Duration != null && uc3Duration > 0) {
        return deriveUnitsForFirstAssignment({
            assignees,
            capacities,
            assigneeCalendars,
            calendar,
            startDate,
            dueDate: toUniversalDateTime(nameof<ITaskAttrs>("DueDate") in update ? update.DueDate : task.attributes.DueDate),
            duration: uc3Duration,
            effort: uc3Effort,
        });
    }

    // Trigger detection uses `primary` (the user's edit). Recompute Effort (keep Duration) when the Duration/span
    // is set explicitly (Duration or Due date) or resources are ADDED/REMOVED (non-Effort-Driven). Otherwise
    // PRESERVE Effort and re-derive Duration: a Start move, a predecessor change, a per-assignment Units edit, or
    // an Effort-Driven resource change. Duration/Due always win over a simultaneous Start (ID 8 / 8.1).
    // NB: an Effort edit is intentionally NOT a trigger — the client sends only Effort and lets the server derive
    // Duration/Due (see the header note). Emitting a client-derived Duration would flip the server into
    // recompute-Effort and inflate it, because the two directions aren't inverses under per-assignment Units.
    const durationOrDueChanged = nameof<ITaskAttrs>("Duration") in primary || nameof<ITaskAttrs>("DueDate") in primary;
    // Whether the SPAN was set explicitly by the user — the question the Effort basis and the span extension below ask,
    // which is about the span `derived` already carries rather than about which edit triggered this recalc.
    const spanEdited = options?.spanEdited ?? durationOrDueChanged;
    // Bug 17370 – a REMOVAL keeps what the SURVIVORS hold (mirror of the engine's keepSurvivorsEffort). Recomputing
    // Effort over the stored span only agrees with that while the span is exactly what the survivors work; one
    // STRETCHED by the departing resource is not, and refilling the survivor's idle days doubles its own work because
    // somebody ELSE left. storedShareOf answers what they hold: the real rows where the panel has them, the shape the
    // rows must be in where it does not.
    const removedCount = [...oldIds].filter(id => !newIds.includes(id)).length;
    // A PURE removal only, mirroring the engine's removalKeepingShares (`oldIds.IsProperSupersetOf(newIds)`).
    // Bug 17377 – a SWAP is not one: it brings in a resource that has to RECEIVE a share, and reading it as a removal
    // gave the newcomer none. A swap is remove then add (UC6 then UC5), and the add recomputes.
    const isPureRemoval = removedCount > 0 && newIds.every(id => oldIds.has(id));
    const survivorsKeepTheirEffort = isFixedUnits && assigneeChanged && membershipChanged && !durationOrDueChanged
        && !effortDrivenResourceChange && isPureRemoval && oldIds.size > removedCount
        && (task.attributes.Effort ?? 0) > 0;
    const survivorsEffort = survivorsKeepTheirEffort
        ? storedShareOf(newIds, task, calendar, resources, calendarsById, useResourceCalendar, storedShares)
        : undefined;
    // Bug 17384 – an ADD keeps what the others hold and derives only the NEWCOMER's share (mirror of the engine's
    // joinerWithDerivedEffort). Recomputing every share from the span re-expands an assignee that finished its work
    // early in a stretched span. Needs the prior shares to be knowable — without them there is nothing to keep, and
    // the recompute below stays the answer.
    const priorShareMap = isFixedUnits && assigneeChanged && membershipChanged && !durationOrDueChanged
        && !effortDrivenResourceChange && newIds.some(id => !oldIds.has(id)) && (task.attributes.Duration ?? 0) > 0
        ? storedSharesById(task, calendar, resources, calendarsById, useResourceCalendar, storedShares)
        : undefined;
    const storedStart = toUniversalDateTime(task.attributes.StartDate);
    const joinerShares = priorShareMap && storedStart
        ? assignees.reduce((map, a, i) => {
            map[a.id] = priorShareMap[a.id] ?? hoursOver(capacities[i], storedStart, task.attributes.Duration!, calendar);
            return map;
        }, {} as Dictionary<number>)
        : undefined;
    const joinersKeepPriorShares = joinerShares != null;
    if (options?.sharesUsed && joinerShares) {
        options.sharesUsed.value = joinerShares;
    }
    const recomputeEffort = !survivorsKeepTheirEffort && !joinersKeepPriorShares
        && (durationOrDueChanged || (assigneeChanged && membershipChanged && !effortDrivenResourceChange));
    const preserveEffort = !recomputeEffort
        && (nameof<ITaskAttrs>("StartDate") in primary
            || nameof<ITaskAttrs>("Predecessor") in primary
            || effortDrivenResourceChange
            || survivorsKeepTheirEffort
            || joinersKeepPriorShares
            || unitsOnlyChange);

    if (!recomputeEffort && !preserveEffort) {
        return undefined;
    }

    // Effort is never a preserve trigger (see above), so the Effort to preserve is the current one — read from the
    // MERGED state, `update` first, for the same reason UC3 does: on the CREATE panel `task` holds new-task defaults
    // with no Effort at all, and the Effort a Units edit must preserve is the one the user just typed.
    const effortToPreserve = survivorsEffort
        ?? (joinerShares && truncate2(Object.values(joinerShares).reduce((sum, hours) => sum + hours, 0)))
        ?? (nameof<ITaskAttrs>("Effort") in update ? update.Effort : task.attributes.Effort);
    // Nothing to preserve: on a STORED task there is nothing to preview either (unchanged), but on an unsaved one the
    // span the user typed is what defines the Effort — and is what the server will store, since a create payload always
    // carries a Duration and therefore takes ApplyFixedUnitsRules' recompute path. Fall through to that recompute
    // rather than leaving the panel with no Effort at all.
    if (preserveEffort && effortToPreserve == null && task.id) {
        return undefined;
    }
    const input: RecalcInputs = {
        sharesOverride: joinerShares,
        task, update, primary, assignees, startDate, capacities, assigneeCalendars, isFixedUnits,
        scheduling, options,
    };
    const triggers: RecalcTriggers = { assigneeChanged, membershipChanged, unitsOnlyChange, spanEdited, survivorsKeepTheirEffort };
    if (preserveEffort && effortToPreserve != null) {
        const preserved = previewPreservedEffortSpan(input, triggers, effortToPreserve);
        // A removal is the one preserve trigger that LOWERS the pool, and the panel has to show that: every other
        // one leaves Effort where it is, so this path writes the span alone and the stored total would stand.
        return preserved && (survivorsKeepTheirEffort || joinersKeepPriorShares)
            ? {
                ...preserved,
                Effort: effortToPreserve,
                RemainingWork: Math.max(0, effortToPreserve - (task.attributes.CompletedWork ?? 0)),
            }
            : preserved;
    }

    // Flexible (ID 5): a Duration / Effort / date / add-remove change re-derives each assignee's Units =
    // (Effort ÷ N) ÷ their working capacity over the Duration × 100 (a Units edit is handled above). Mirrors the
    // server's ApplyFlexibleDerivedUnits. IMPORTANT: derived Units are DISPLAY-only — never put them in the save
    // payload. With no persisted assignments the engine reads a stated Units as the user's own and derives the span
    // FROM it (TryApplyUnitsEditRules), so echoing a DERIVED one back would recompute the schedule from a number
    // nobody typed — a 25%-Units assignee needs 2 days for its share, so a Duration 1 edit reads back as 3. The edit
    // panel routes this through its DISPLAY-only derivation (task Fields.buildDeriveDisplayAttributes); the save sends
    // only the raw edit.
    if (!isFixedUnits) {
        return previewFlexibleUnitsAndSpan(input, triggers);
    }

    return previewFixedUnitsRecompute(input, triggers);
}

/**
 * PRESERVE Effort: the span follows each assignee's own Units×capacity (a Units edit, a Start move, a predecessor
 * change or an Effort-Driven resource change). Split out of applyFixedUnitsRecalc unchanged.
 */
function previewPreservedEffortSpan(input: RecalcInputs, triggers: RecalcTriggers, effortToPreserve: number): Partial<ITaskAttrs> | undefined {
    const { task, update, primary, assignees, startDate, capacities, assigneeCalendars, isFixedUnits, options } = input;
    const { calendar, resources, calendarsById, useResourceCalendar, storedShares } = input.scheduling;
    const { unitsOnlyChange } = triggers;

    if (!isFixedUnits && !unitsOnlyChange) {
        // Flexible: non-Units triggers derive Units, not Duration — with one exception.
        //
        // Bug 17182 – a Start MOVE does not carry an EXTENDED span along with it. The engine takes the
        // distribution-preserving shift path, re-lays each assignee's EXISTING rows on THEIR OWN calendar from
        // the new Start, and puts the Due on the last of those rows — in BOTH directions, because a bare move
        // asks for no span of its own (contractSpanToEffort). The stored Duration is a UNION count of a span the
        // assignees stretched, so walking THAT from the new Start is a different walk entirely.
        //
        // The row count is the span's REAL duration (getRealDuration), and re-walking it per assignee is
        // calculateDueDate's MAX — the same two steps previewFlexibleEffortEdit mirrors for an Effort edit, only
        // anchored at the NEW Start.
        //
        // FIXED UNITS needs none of this: its rows are rebuilt from each assignee's preserved Effort at their
        // Units (ShiftAssignmentByDays' Fixed Units branch), which is the per-assignee-MAX model below.
        const storedStart = toUniversalDateTime(task.attributes.StartDate);
        const storedDue = toUniversalDateTime(task.attributes.DueDate);
        const storedDuration = task.attributes.Duration;
        if (!useResourceCalendar || !(nameof<ITaskAttrs>("StartDate") in primary)
            || !storedStart || !storedDue || storedDuration == null || storedDuration <= 0) {
            return undefined;
        }
        const rowDays = getRealDuration(storedStart, storedDue, storedDuration, assigneeCalendars, calendar);
        const reprojectedDue = calculateDueDate(startDate, rowDays, calendar, assigneeCalendars);
        // Extend always; CONTRACT only while nothing else claims the Due (the engine's contractSpanToEffort — a
        // predecessor-dictated Due is honored, and only ever pushes the span out).
        const movedDue = toUniversalDateTime(nameof<ITaskAttrs>("DueDate") in update ? update.DueDate : task.attributes.DueDate);
        const movedOrReprojectedDue = movedDue && reprojectedDue < movedDue && !(options?.mayContract ?? true) ? movedDue : reprojectedDue;
        return {
            Duration: getDurationBetween(startDate, movedOrReprojectedDue, assigneeCalendars, calendar),
            DueDate: movedOrReprojectedDue.toUniversalIsoDateTimeString()
        };
    }

    // US 15956 UC15/UC16 – a 0%-Units assignee contributes no effort and is excluded from the span/effort
    // computation (mirrors server ApplyZeroUnitsEdit). Kept minimal since the server is authoritative on save.
    const activeCapacities = capacities.filter(c => c.unitsFactor > 0);
    if (activeCapacities.length === 0) {
        // UC15: no active assignee → Effort and Duration collapse to 0.
        return { Duration: 0, DueDate: startDate.toUniversalIsoDateTimeString(), Effort: 0, RemainingWork: Math.max(0, -(task.attributes.CompletedWork ?? 0)) };
    }
    const hasZeroed = activeCapacities.length < capacities.length;
    if (hasZeroed && !isFixedUnits) {
        // UC16 Flexible: the pool is preserved and re-split over the CURRENT Duration (Units re-derived on
        // save); Duration/Effort are unchanged, so there is nothing to preview here.
        return undefined;
    }

    // US 15956 – per-assignee-MAX model (units edit, start move, Effort-Driven add): each assignee's equal
    // Effort share spans their OWN Units×capacity and the task Due is the LATEST active-assignee end, with
    // Duration spanning Start→Due so the two stay consistent.
    // Bug 17180 – WHO that share is split between depends on when the zeroing happened. The Units edit ITSELF
    // still carries the pre-drop total, so each assignee keeps the share they already held (Effort ÷ N) and the
    // task Effort falls to the active part of it (UC16 Fixed). Every OTHER preserve trigger runs on a task whose
    // Effort is ALREADY the active total, where splitting by N again would drop it a second time. Nor does the
    // drop apply when the payload STATES an Effort: the server hands that combination to the Effort rules so the
    // typed value survives whole (Bug 17158).
    const dropsZeroedShare = hasZeroed && isFixedUnits && unitsOnlyChange && !options?.effortStated;
    // Bug 17192 / Bug 17193 – the share is split between the assignees that can actually WORK, which is not the
    // same question as the 0%-Units one above: a resource with no capacity at all has no day to put work ON, so
    // the engine gives it no rows and a share handed to it is LOST rather than misplaced. Mirrors the server's
    // CarriesEffortFrom. Skipping them in the span loop also avoids walking a dayless calendar to
    // getFixedUnitsDuration's day guard — ~0.3s per derivation, on every keystroke.
    const workingCapacities = activeCapacities.filter(c => hasWorkingDayFrom(startDate, [c.calendar]));
    if (workingCapacities.length === 0) {
        // Nobody can work it: the server's contributor set is empty too, so its span never leaves the Start and
        // the distribution leaves no rows for the totals to be re-derived from — the same collapse as UC15.
        return { Duration: 0, DueDate: startDate.toUniversalIsoDateTimeString(), Effort: 0, RemainingWork: Math.max(0, -(task.attributes.CompletedWork ?? 0)) };
    }
    const perAssignee = effortToPreserve / ((dropsZeroedShare ? capacities.length : workingCapacities.length) || 1);
    // Bug 17385 – EVERY Effort-preserving trigger keeps the share each assignee already HOLDS and only re-spans it,
    // never an even re-split: a Units edit (Bug 17374), a removal (Bug 17375), a Start move or a predecessor cascade,
    // which touch nobody at all. Mirror of the engine's EffortShares, including the two tests that gate it — an
    // active assignee holding nothing is a share to re-MAKE rather than keep (Bug 17211, a resource coming back from
    // 0% Units), and the shares must ACCOUNT for the pool.
    const priorShares = input.sharesOverride
        ?? (isFixedUnits && !dropsZeroedShare
            ? storedSharesById(task, calendar, resources, calendarsById, useResourceCalendar, storedShares)
            : undefined);
    const shareByCapacity = new Map<AssigneeCapacity, number>();
    capacities.forEach((c, i) => {
        const share = priorShares?.[assignees[i]?.id];
        if (share != null) {
            shareByCapacity.set(c, share);
        }
    });
    // ...only while those shares ACCOUNT for the pool being spanned, the engine's own test made over the assignees
    // actually being measured. A pool they cannot account for is one that was RESTATED — a direct Effort edit — and
    // the even split is then right. It is also what keeps Effort Driven re-splitting: there a removal preserves the
    // WHOLE pool, so a survivor's stored share no longer describes it (UC6).
    const sharesTotal = workingCapacities.reduce((sum, c) => sum + (shareByCapacity.get(c) ?? 0), 0);
    const keepsPriorShares = priorShares != null
        && workingCapacities.every(c => (shareByCapacity.get(c) ?? 0) > 0)
        && Math.abs(sharesTotal - effortToPreserve) <= DURATION_COMPARISON_EPSILON;
    let dueDate = startDate;
    for (const c of workingCapacities) {
        const share = keepsPriorShares ? shareByCapacity.get(c)! : perAssignee;
        const end = getAssigneeSpanEnd(startDate, getFixedUnitsDuration(startDate, share, [c]), c.calendar, calendar);
        if (end > dueDate) {
            dueDate = end;
        }
    }
    // Duration spans Start→Due over ALL assignee calendars, including a 0%-Units one: it no longer drives the
    // Due, but it is still on the task, so its working days count towards the span the task occupies (the server
    // passes the full assigneeCalendars to GetDuration too). Measuring over the ACTIVE calendars alone
    // under-counts a day only the zeroed assignee works, leaving a Duration that contradicts its own Start/Due.
    const spanDuration = getDurationBetween(startDate, dueDate, assigneeCalendars, calendar);
    const spanResult: Partial<ITaskAttrs> = { Duration: spanDuration, DueDate: dueDate.toUniversalIsoDateTimeString() };
    if (dropsZeroedShare) {
        // UC16 Fixed: dropping the 0%-Units assignees lowers the total Effort to the active share.
        const droppedEffort = truncate2(perAssignee * activeCapacities.length);
        spanResult.Effort = droppedEffort;
        spanResult.RemainingWork = Math.max(0, droppedEffort - (task.attributes.CompletedWork ?? 0));
    }
    return spanResult;
}

/**
 * Flexible (ID 5): Units are DERIVED from the Effort each assignee carries over the span, and a membership change
 * re-projects the span onto the new assignees' own days. Split out of applyFixedUnitsRecalc unchanged.
 */
function previewFlexibleUnitsAndSpan(input: RecalcInputs, triggers: RecalcTriggers): Partial<ITaskAttrs> | undefined {
    const { task, update, assignees, startDate, capacities, assigneeCalendars, options } = input;
    const { calendar, resources, calendarsById, useResourceCalendar } = input.scheduling;
    const { assigneeChanged, membershipChanged, spanEdited } = triggers;

    const flexEffort = nameof<ITaskAttrs>("Effort") in update ? update.Effort : task.attributes.Effort;
    let flexDuration = nameof<ITaskAttrs>("Duration") in update ? update.Duration : task.attributes.Duration;
    // Only the UNITS derivation needs a POSITIVE Effort to divide up (the server guards it the same way —
    // ApplyFlexibleDerivedUnits returns early on `(task.Effort ?? 0) <= 0`, leaving Units exactly as sent). The span
    // EXTENSION below is a function of the Duration and the calendars, so `> 0` is the wrong gate for it: adding a
    // Wed–Fri resource to a 3d Mon→Wed task with Effort **0** previewed no change at all where the engine still
    // reprojects the rows onto that resource’s own Wed–Fri and stores Mon→Fri / 5d.
    // What it IS gated on is Effort being SET at all (Bug 17141). An Effort of 0 is a value and gets distributed —
    // 0-hour rows on each assignee's own days, which the span then follows — but an UNSET Effort makes the engine
    // skip the reprojection entirely: RecalculateEffort's UC2 guard (`!task.Effort.HasValue && !recomputeEffort &&
    // !stats.HasEffortDates`) hands each assignee null-effort rows over the task's OWN span instead of walking their
    // calendars, so there is nothing to extend to. A Flexible 2d Thu task with no Effort previewed 4d / Tue 08-25
    // after a Mon–Wed and a Wed–Fri resource were added, where the engine keeps 2d / Fri 08-21. Only FLEXIBLE reads
    // it this way — Fixed Units sets recomputeEffort (it derives Effort from the span), so its own extension below
    // runs whether an Effort was entered or not.
    if (flexDuration == null || flexDuration <= 0 || assignees.length === 0) {
        return undefined;
    }
    const derivesUnits = flexEffort != null && flexEffort > 0;

    // Resource ADD/REMOVE under resource calendars: the server re-lays every assignee's rows over the span's real
    // duration on THEIR OWN calendar, puts the Due on the latest of them, then re-derives Units over the
    // resulting Duration — so a 1-day/2-assignee task with one assignee off the only day becomes 2 days at
    // 25%/25%. Membership changes only, since a Duration/date edit keeps the user's span, and the reprojected
    // Due/Duration are PREVIEW-only (never sent).
    //
    // `!spanEdited` (same guard as the Fixed Units extension below) keeps this from extending an ALREADY-extended
    // span: handleDuration derives the Due of a Duration edit with calculateDueDate, whose per-assignee MAX walk
    // performs exactly this extension itself, and doing it twice compounds.
    //
    // Bug 17160 – the count handed out is the span's REAL duration, never the stored Duration, which on an
    // already-extended task is a UNION count no single assignee has. The engine normalizes exactly this
    // (NormalizeFlexibleDurationForOwnDayReprojection), measured over the PERSISTED set — the assignees the span
    // was extended FOR. Measuring an ADD's newcomer would narrow a span that was never extended (Bug 17153),
    // while on a REMOVAL the departing resource is still an owner, because the MIN is the real duration only
    // while that set is whole (Bug 17156).
    //
    // Bug 17208 – a REMOVAL reads that basis too, and the reprojection goes BOTH ways as the engine's does: it
    // re-derives the Due from the normalized Duration rather than extending the stored one. Removing the resource
    // that DICTATED the span is the case needing the other direction. Contraction is off while a predecessor
    // claims the Due (`mayContract`), and a user-stated span is already excluded by `!spanEdited`.
    let reprojectedDue: Date | undefined;
    if (useResourceCalendar && assigneeChanged && membershipChanged && !spanEdited && flexEffort != null) {
        const currentDue = toUniversalDateTime(nameof<ITaskAttrs>("DueDate") in update ? update.DueDate : task.attributes.DueDate);
        const previousAssignees = task.attributes.AssignedTo ?? [];
        const storedDue = toUniversalDateTime(task.attributes.DueDate);
        // With nobody assigned before this edit there is no owner to measure: a first assignment keeps the
        // Duration the task was authored with (getAssigneeCalendars would stand the GLOBAL calendar in for the
        // empty set and measure the project's own days, which is not a basis anybody holds).
        const basis = previousAssignees.length > 0 && storedDue
            ? getRealDuration(startDate, storedDue, flexDuration,
                getAssigneeCalendars(useResourceCalendar, previousAssignees, calendar, resources, calendarsById), calendar)
            : flexDuration;
        const candidateDue = calculateDueDate(startDate, basis, calendar, assigneeCalendars);
        const mayContract = options?.mayContract ?? true;
        const spanChanged = currentDue && (candidateDue.getTime() > currentDue.getTime()
            || (mayContract && candidateDue.getTime() < currentDue.getTime() && candidateDue.getTime() >= startDate.getTime()));
        if (spanChanged) {
            reprojectedDue = candidateDue;
            flexDuration = getDurationBetween(startDate, candidateDue, assigneeCalendars, calendar);
        }
    }

    const flexibleResult: Partial<ITaskAttrs> = {};
    if (reprojectedDue) {
        flexibleResult.Duration = flexDuration;
        flexibleResult.DueDate = reprojectedDue.toUniversalIsoDateTimeString();
    }
    if (derivesUnits) {
        // Capacity is measured over the REAL duration (the days the assignees actually share inside the span), not
        // the raw Duration — see getRealDuration. Mirrors the server's ApplyFlexibleDerivedUnits.
        // Bug 17202 – split between the assignees that carry EFFORT, not all of them: the server derives Flexible
        // Units from the assignment ROWS, and RecalculateEffort gives a no-capacity resource none — so assigning a
        // Mon–Fri resource and a 0-capacity one to a 3d/24h task stores 100% Units for the Mon–Fri resource, where
        // an even split over both previewed a 12h share and 50%. See carriesEffortFrom.
        const perAssignee = flexEffort! / (capacities.filter(c => carriesEffortFrom(startDate, c)).length || assignees.length);
        const flexDue = reprojectedDue ?? toUniversalDateTime(nameof<ITaskAttrs>("DueDate") in update ? update.DueDate : task.attributes.DueDate);
        const unitsDuration = getRealDuration(startDate, flexDue, flexDuration!, assigneeCalendars, calendar);
        flexibleResult.AssignedTo = assignees.map((a, i) => {
            const capacity = capacityHoursOver(capacities[i], startDate, unitsDuration, calendar);
            return { ...a, units: capacity > 0 ? truncate2(perAssignee / capacity * 100) : undefined };
        });
    }
    return Object.keys(flexibleResult).length > 0 ? flexibleResult : undefined;
}

/**
 * Fixed Units RECOMPUTE: the span the user stated drives the Effort (Effort = Σ Units × capacity over the
 * Duration). Split out of applyFixedUnitsRecalc unchanged.
 */
function previewFixedUnitsRecompute(input: RecalcInputs, triggers: RecalcTriggers): Partial<ITaskAttrs> | undefined {
    const { task, update, startDate, capacities, assigneeCalendars } = input;
    const { calendar, useResourceCalendar } = input.scheduling;
    const { spanEdited } = triggers;

    const duration = (nameof<ITaskAttrs>("Duration") in update ? update.Duration : task.attributes.Duration);
    if (duration === undefined || duration === null) {
        return undefined;
    }

    // Mirror server AdjustScheduleDataUpdates: when the user edits the SPAN (Duration/Due) under resource calendars,
    // the Duration that drives Effort is the MIN over assignees of their OWN-calendar working-day span, NOT the
    // combined Duration. An assignee off a day INSIDE the span spans fewer working days, so their Effort share is
    // smaller; the combined calendars count that day for everyone and inflate the Effort.
    //
    // BUT the MIN applies ONLY to an explicit Duration/Due value (`spanEdited`), because the server runs it solely
    // when the update carries Start+Due+Duration together. A resource ADD/REMOVE reaches here only because
    // handleDuration DERIVED a Due, and for that the server walks each assignee's own calendar over the full
    // task.Duration — so a newly-added assignee off the task's only day must not collapse the MIN span to 0.
    const dueForEffort = useResourceCalendar && spanEdited
        ? toUniversalDateTime(nameof<ITaskAttrs>("DueDate") in update ? update.DueDate : task.attributes.DueDate)
        : undefined;
    // Bug 17189 – over the assignees that can SEE the span, the server's own filter here (and getRealDuration's):
    // an assignee with NO capacity holds no working day inside ANY span, so its count is 0 and an unfiltered MIN
    // zeroes the Effort outright. Both shapes of "no capacity" land on it — a work week with no hours, and Max
    // Units 0% / Non-project Work 100% (ID 11). When NOBODY can see the span there is no minimum to take, so the
    // requested Duration stands, the same fallback getRealDuration makes.
    const perAssigneeSpans = dueForEffort && dueForEffort.getTime() > startDate.getTime()
        ? capacities.map(c => getDurationBetween(startDate, dueForEffort, [c.calendar], calendar)).filter(_ => _ > 0)
        : [];
    const durationForEffort = perAssigneeSpans.length > 0 ? Math.min(...perAssigneeSpans) : duration;

    // Effort = Σ assignees (Units × working hours over the Duration) (fractional-day aware). Zero-duration tasks
    // keep zero Effort (ID 7.1). Completed Work is preserved; Remaining Work follows the new Effort (ID 9 / 9.1).
    const effort = durationForEffort <= 0
        ? 0
        : truncate2(capacities.reduce((sum, c) => sum + hoursOver(c, startDate, durationForEffort, calendar), 0));
    const result: Partial<ITaskAttrs> = {
        Effort: effort,
        RemainingWork: Math.max(0, effort - (task.attributes.CompletedWork ?? 0))
    };

    // Resource ADD/REMOVE under resource calendars: on recompute each assignee works the FULL Duration on THEIR OWN
    // calendar and the server EXTENDS the span to the latest assignee end, so a newly-added assignee off the current
    // span pushes the Due to their next working day. handleDuration only re-fits the Due to the combined calendars,
    // so mirror the server here or the preview shows the old span. Membership changes only (an edited span keeps the
    // user's), and EXTEND only, so a REMOVE keeps the existing span.
    if (useResourceCalendar && !spanEdited && effort > 0 && duration > 0) {
        const currentDue = toUniversalDateTime(nameof<ITaskAttrs>("DueDate") in update ? update.DueDate : task.attributes.DueDate);
        const dueDate = calculateDueDate(startDate, duration, calendar, assigneeCalendars);
        if (currentDue && dueDate.getTime() > currentDue.getTime()) {
            result.Duration = getDurationBetween(startDate, dueDate, assigneeCalendars, calendar);
            result.DueDate = dueDate.toUniversalIsoDateTimeString();
        }
    }
    return result;
}

// Bug 17141 – an Effort edit on an AUTO FLEXIBLE task reconciles the SPAN as well as the Units, because setting an
// Effort is what makes the engine reproject at all (see reprojectsOntoAssigneeDays). Two engine steps, in order:
//   1. NormalizeFlexibleDurationForEffortEdit narrows Duration to the per-assignee MIN of the days each assignee
//      really has inside the stored span (getRealDuration — it only ever shrinks, and falls back to the stored value
//      when nobody can see the span at all).
//   2. The reprojection then hands EVERY assignee that many of their OWN days — starting at their next working day
//      when the Start is not one of theirs — and stretches the span to the latest of those days, which is exactly
//      what calculateDueDate's per-assignee MAX walk computes.
// A 2d Thu→Fri task with a Mon–Tue–Wed and a Wed–Thu–Fri resource stores 4d / Tue 08-25 the moment an Effort is
// entered (each needs two of their own days), where the panel changed nothing but the Units. Gated exactly like the
// server: Flexible, auto, leaf, an Effort in the update, no span key alongside it (the create panel's echoed triple
// takes the same route through AdjustScheduleDataUpdates' own MIN and lands on the same numbers), and assignees.
// Effort 0 reconciles too — the engine's normalization is not gated on the amount, only the Units derivation is.
export function previewFlexibleEffortEdit(
    task: ITask,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>, calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean): Partial<ITaskAttrs> | undefined {

    if (!useResourceCalendar
        || !task.isAutoMode
        || task.hierarchy.isParent
        || task.taskType !== TaskType.Flexible
        || task.attributes.Effort == null) {
        return undefined;
    }
    const assignees = task.attributes.AssignedTo ?? [];
    const startDate = toUniversalDateTime(task.attributes.StartDate);
    const dueDate = toUniversalDateTime(task.attributes.DueDate);
    const duration = task.attributes.Duration;
    if (assignees.length === 0 || !startDate || !dueDate || duration == null || duration <= 0) {
        return undefined;
    }
    const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, assignees, calendar, resources, calendarsById);
    const basis = getRealDuration(startDate, dueDate, duration, assigneeCalendars, calendar);
    const reprojectedDue = calculateDueDate(startDate, basis, calendar, assigneeCalendars);
    return {
        Duration: getDurationBetween(startDate, reprojectedDue, assigneeCalendars, calendar),
        DueDate: reprojectedDue.toUniversalIsoDateTimeString()
    };
}

// US 15956 – compute the Duration/Due that an Effort edit WILL produce on the server (the per-assignee-MAX
// preserve model), for DISPLAY ONLY on the edit panel (used by Fields.buildDeriveDisplayAttributes). This must
// NEVER be sent to the server (see the applyFixedUnitsRecalc header). Returns undefined for non-Fixed-Units /
// manual / summary / no-assignee / no-Start tasks (nothing to derive).
export function previewFixedUnitsEffortEdit(
    project: ProjectInfo,
    task: ITask,
    effort: number | null | undefined,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>, calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean): Partial<ITaskAttrs> | undefined {

    if (!task.isAutoMode
        || task.hierarchy.isParent
        || project.settings.progressCalculationType !== ProgressCalculationType.Effort
        || task.taskType !== TaskType.FixedUnits
        || effort === undefined || effort === null) {
        return undefined;
    }
    const assignees = task.attributes.AssignedTo ?? [];
    const startDate = toUniversalDateTime(task.attributes.StartDate);
    if (assignees.length === 0 || !startDate) {
        return undefined;
    }
    const capacities = getPerAssigneeCapacities(assignees, calendar, resources, calendarsById, useResourceCalendar);
    const assigneeCalendars = capacities.map(c => c.calendar);
    // Bug 17180 – the share is split over the assignees that CAN work it: a 0%-Units one contributes nothing
    // (UC15/UC16) and gets no distributed effort, so counting it as a participant measures the span against a
    // HALVED Effort — 24h over a 100% + a 0% assignee previewed 1.5d where the single active resource owns all
    // 24h and needs 3 days. Mirrors server ApplyFixedUnitsRules' per-assignee-MAX span. The Duration still spans
    // Start→Due over ALL assignee calendars (the zeroed assignee is still on the task — see applyFixedUnitsRecalc).
    // Bug 17193 – and over the ones that can actually WORK it, which the Units test alone does not answer. A
    // resource with no capacity (0-hour work week, Max Units 0%) is given no rows by the engine, so its share is
    // lost, not merely misplaced: this is the very preview in the report — a 1d/8h task re-stated to 24h showed
    // 1.5d and saved 12h, where all 24h belong to the one resource that can work and take 3 days. Mirrors the
    // server's CarriesEffortFrom contributor set.
    const activeCapacities = capacities.filter(c => carriesEffortFrom(startDate, c));
    const perAssignee = activeCapacities.length > 0 ? effort / activeCapacities.length : 0;
    let dueDate = startDate;
    for (const c of activeCapacities) {
        const end = getAssigneeSpanEnd(startDate, getFixedUnitsDuration(startDate, perAssignee, [c]), c.calendar, calendar);
        if (end > dueDate) {
            dueDate = end;
        }
    }
    return {
        Duration: getDurationBetween(startDate, dueDate, assigneeCalendars, calendar),
        DueDate: dueDate.toUniversalIsoDateTimeString()
    };
}

// The task's REAL duration — the MIN across assignees of the working days each of them actually has inside
// [Start, Due]. Mirrors the server's TaskWorkloadHandler.GetRealDuration. `Duration` counts GLOBAL working days, but
// getWorkingHoursForDuration walks that count on the assignee's OWN calendar (skipping their non-working days and
// never stopping at the Due), so for a short/offset work week it credits capacity BEYOND the task — a Mon–Wed
// resource on a Mon→next-Tue span gets a third week. Falls back to `duration` when the span/calendars are unusable,
// and never exceeds it; for uniform calendars it IS `duration`, so only heterogeneous-calendar projects differ.
export function getRealDuration(
    startDate: Date,
    dueDate: Date | undefined,
    duration: number,
    assigneeCalendars: CalendarDataSet[],
    calendar: CalendarDataSet): number {

    if (!dueDate || duration <= 0 || assigneeCalendars.length === 0) {
        return duration;
    }
    const assigneeWorkingDays = assigneeCalendars
        .map(_ => getDurationBetween(startDate, dueDate, [_], calendar))
        .filter(_ => _ > 0);
    return assigneeWorkingDays.length ? Math.min(Math.min(...assigneeWorkingDays), duration) : duration;
}

// Bug 17156 – the assignees a STORED span was EXTENDED for: the ones the edit leaves on the task, plus the ones it
// REMOVES. This is the set getRealDuration has to measure to recover the duration that span was extended from, and
// mirrors the server's GetSpanOwnerCalendars (which reads the departing assignees off the persisted rows). The span
// extension gives EVERY assignee `Duration` of their own days and keeps the latest end (calculateDueDate's per-assignee
// MAX), so inside the stored span the assignee that dictated the Due has exactly that many own days and everyone else
// has MORE — the MIN is the real duration only while the whole set is measured. Taken over the survivors alone, a
// REMOVAL could still GROW the task: dropping the Wed–Fri resource from a 5d/40h Mon 08-03 task shared with a Mon–Wed
// one (stored extended to Thu 08-13 / 9d, 50% each) previewed the survivor's SIX days inside that span — 6d at 83.33%
// — where the answer is the 5d at 100% the task was authored as. Returns the NEXT set unchanged when nothing was
// removed, so an add-only edit is untouched.
export function getSpanOwners(previous: IUserInfo[] | undefined, next: IUserInfo[] | undefined): IUserInfo[] {
    const owners = [...(next ?? [])];
    (previous ?? []).forEach(_ => {
        if (!owners.some(o => o.id === _.id)) {
            owners.push(_);
        }
    });
    return owners;
}

// US 15956 – on a FLEXIBLE task per-assignee Units are DERIVED: Units% = (Effort ÷ N) ÷ (that assignee's working
// capacity over the task's REAL duration) × 100. Pure function of (assignees, effort, duration, start, due) — used
// by the panel's central display derivation to show Units live for ANY scheduling edit (duration/date/effort/
// assignee change). Mirrors the server's ApplyFlexibleDerivedUnits, including its getRealDuration basis: measuring
// against the raw Duration inflates the denominator for a short-week assignee (a 7-day span read 71.42% where the
// server stores 100%). A zero-capacity assignee (off the whole span) gets undefined Units (the 100% default).
// NEVER sent to the server — the server re-derives Units authoritatively on save.
export function deriveFlexibleUnits(
    assignees: IUserInfo[],
    effort: number,
    duration: number,
    startDate: Date,
    dueDate: Date | undefined,
    scheduling: SchedulingInputs): IUserInfo[] {

    const { calendar, resources, calendarsById, useResourceCalendar } = scheduling;
    const capacities = getPerAssigneeCapacities(assignees, calendar, resources, calendarsById, useResourceCalendar);
    const realDuration = getRealDuration(startDate, dueDate, duration, capacities.map(_ => _.calendar), calendar);
    // Bug 17202 – over the assignees that carry EFFORT (carriesEffortFrom), which is the set RecalculateEffort's equal
    // split hands rows to and therefore the set CalculateDerivedUnits measures. Falls back to the full list when NOBODY
    // can work it, matching the server's own degenerate case (it keeps the even split rather than zeroing every row);
    // every such assignee has 0 capacity anyway, so they all derive undefined Units either way.
    const perAssignee = effort / (capacities.filter(c => carriesEffortFrom(startDate, c)).length || assignees.length || 1);
    return assignees.map((a, i) => {
        const capacity = capacityHoursOver(capacities[i], startDate, realDuration, calendar);
        return { ...a, units: capacity > 0 ? truncate2(perAssignee / capacity * 100) : undefined };
    });
}

// Bug 17223 – mirror of the server's PinReprojectedSpanToDictatedDue. Every span the recalcs above derive is anchored
// at the START and walked forward, so a smaller Effort (or Units) pulls the Due back. A to-Finish predecessor (FF/SF)
// dictates that Due, and the engine only ever lets a task finish LATER than the dictated instant: the shortening
// belongs on the START instead, exactly as handleDuration's shiftDueDate already resolves a DURATION edit under the
// same pin. Previewing the shrunken Due instead showed the task jumping off its predecessor and tripped the panel's
// own "start date is set by a predecessor" warning, while the save was rejected outright.
//
// Walked with calculateMovedStartDate — the COMBINED-calendar walk the server's CalculateSpanStartDate is, not the
// per-assignee MIN: this preserves the Duration the Effort just produced rather than extending it again.
export function pinSpanToDictatedDue(
    attrs: Pick<ITaskAttrs, "DueDate" | "Duration">,
    dictatedDueDate: Date | undefined,
    globalCalendar: CalendarDataSet,
    calendars: CalendarDataSet[]): Partial<ITaskAttrs> | undefined {

    const dueDate = toUniversalDateTime(attrs.DueDate);
    const duration = attrs.Duration;
    // Only ever pushes the Due OUT: a span that already reaches the dictated instant is left exactly as it is, which
    // is every recompute-Effort case and anything the user stated a Duration/Due for.
    if (!dictatedDueDate || !dueDate || duration == null || dictatedDueDate <= dueDate) {
        return undefined;
    }
    return {
        StartDate: calculateMovedStartDate(dictatedDueDate, duration, globalCalendar, calendars).toUniversalIsoDateTimeString(),
        DueDate: dictatedDueDate.toUniversalIsoDateTimeString()
    };
}

function shiftDueDate(startDate: Date, newDurationDays: number, dictatedDueDate: Date, globalCalendar: CalendarDataSet, calendars: CalendarDataSet  [], extra: Partial<IWithDuration>) {
    const newDueDate = calculateDueDate(startDate, newDurationDays, globalCalendar, calendars);
    if (newDueDate < dictatedDueDate) {
        extra.StartDate = calculateStartDate(dictatedDueDate, newDurationDays, globalCalendar, calendars).toUniversalIsoDateTimeString();
        extra.DueDate = dictatedDueDate.toUniversalIsoDateTimeString();
    } else {
        extra.StartDate = startDate.toUniversalIsoDateTimeString();
        extra.DueDate = newDueDate.toUniversalIsoDateTimeString();
    }
}

function shiftStartDate(dueDate: Date, newDurationDays: number, dictatedStartDate: Date, globalCalendar: CalendarDataSet, calendars: CalendarDataSet[], extra: Partial<IWithDuration>) {
    const newStartDate = calculateStartDate(dueDate, newDurationDays, globalCalendar, calendars);
    if (newStartDate < dictatedStartDate) {
        extra.StartDate = dictatedStartDate.toUniversalIsoDateTimeString();
        extra.DueDate = calculateDueDate(dictatedStartDate, newDurationDays, globalCalendar, calendars).toUniversalIsoDateTimeString();
    } else {
        extra.StartDate = newStartDate.toUniversalIsoDateTimeString();
        extra.DueDate = dueDate.toUniversalIsoDateTimeString();
    }
}

// The counterpart date for a MOVE, i.e. an edit that PRESERVES the task's Duration (a Start/Due date
// change with no Duration change, a Gantt drag-move, a predecessor-dictated shift). `Duration` is a COMBINED
// working-day count — it is measured with getDurationBetween over ALL assignee calendars at once — so the date that
// preserves it must be found by walking that same combined metric, of which addDuration/subtractDuration over the
// full calendar list is the exact inverse. The derived Duration then round-trips to the value being preserved.
//
// NOT calculateDueDate/calculateStartDate: those take the MAX/MIN of each assignee's OWN-calendar walk of the count,
// which is the resource-add / Duration-edit semantic (the server's per-assignee span EXTENSION). Applied to a move it
// re-extends an ALREADY-extended span. The server MOVES the same way — its date derivations walk the combined
// calendar and the per-assignee extension happens later, in the reprojection.
function calculateMovedDueDate(startDate: Date, durationDays: number, globalCalendar: CalendarDataSet, calendars: CalendarDataSet[]): Date {
    return addDuration(startDate, Math.abs(durationDays), calendars, globalCalendar);
}

function calculateMovedStartDate(dueDate: Date, durationDays: number, globalCalendar: CalendarDataSet, calendars: CalendarDataSet[]): Date {
    return subtractDuration(dueDate, Math.abs(durationDays), calendars, globalCalendar);
}

export function calculateDueDate(startDate: Date, durationDays: number, globalCalendar: CalendarDataSet, calendars: CalendarDataSet[]): Date {
    durationDays = Math.abs(durationDays);
    if (durationDays === 0) {
        return startDate.clone();
    }
    const assigneeDueDates = calendars.map(c => addDuration(startDate, durationDays, [c], globalCalendar));
    return max(assigneeDueDates)!;
}

export function calculateStartDate(dueDate: Date, durationDays: number, globalCalendar: CalendarDataSet, calendars: CalendarDataSet[]): Date {
    durationDays = Math.abs(durationDays);
    if (durationDays === 0) {
        return dueDate.clone();
    }
    const assigneeStartDates = calendars.map(c => subtractDuration(dueDate, durationDays, [c], globalCalendar));
    return min(assigneeStartDates)!;
}

function calculateDuration(startDate: Date, dueDate: Date, oldDuration: number | null | undefined, globalCalendar: CalendarDataSet, calendars: CalendarDataSet[]): number {
    return oldDuration === 0 && calculateDueDate(startDate, oldDuration, globalCalendar, calendars).getTime() === dueDate.getTime()
        ? oldDuration
        : getDurationBetween(startDate, dueDate, calendars, globalCalendar);
}